import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AiOutlineInfoCircle,
  AiOutlinePicture,
  AiOutlineUserAdd,
  AiOutlineDown,
  AiFillCheckCircle,
  AiOutlineArrowRight,
} from 'react-icons/ai';

import AgendaReserva from '../AgendaReserva';
import Stepper from './Stepper';
import OptionCard from './OptionCard';
import EspacoDetalhesModal from './EspacoDetalhesModal';
import ResumoReserva from './ResumoReserva';

import api from '../../services/api';
import { ITipEsp } from '../../types/TipEsp';
import { IEspaco } from '../../types/Espaco';
import Context, { IContext } from '../../context/Context';
import { cnpjMask, cpfMask } from '../../utils/validaCpfCnpj';
import { IUsuario } from '../../types/Usuario';
import { IFotEsp } from '../../types/FotEsp';
import { ITipRes } from '../../types/TipRes';
import { formatCurrency } from '../../utils/formatCurrency';

interface IFormData {
  cgc: string;
  obs: string;
}

type StatusBuscaUsuario = 'idle' | 'buscando' | 'encontrado' | 'naoEncontrado';

function textoSemHtml(html?: string) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

function menorValorTipoReserva(tipoDeReserva: ITipRes) {
  const valores = [
    tipoDeReserva.valseg,
    tipoDeReserva.valter,
    tipoDeReserva.valqua,
    tipoDeReserva.valqui,
    tipoDeReserva.valsex,
    tipoDeReserva.valsab,
    tipoDeReserva.valdom,
  ].filter(valor => valor > 0);

  return valores.length > 0 ? Math.min(...valores) : undefined;
}

// Somente no mobile: rola suavemente até a próxima seção (ou até o fim da tela) após a seleção
function rolarNoMobile(idSecao?: string) {
  if (!window.matchMedia('(max-width: 767px)').matches) return;

  const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  // Aguarda o React renderizar a próxima seção antes de rolar
  setTimeout(() => {
    if (!idSecao) {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior });
      return;
    }

    document.getElementById(idSecao)?.scrollIntoView({ behavior, block: 'start' });
  }, 100);
}

type EstadoSecao = 'concluida' | 'atual' | 'pendente';

interface ISecaoProps {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoSecao;
  instrucao: string;
  destacar?: boolean;
  children: React.ReactNode;
}

// Cada seção indica visualmente se já foi preenchida, se é a etapa atual (o que o usuário deve fazer agora) ou se ainda está pendente
function Secao({ id, numero, titulo, estado, instrucao, destacar, children }: ISecaoProps) {
  const estilosPorEstado: Record<EstadoSecao, string> = {
    concluida: 'border-zinc-200 bg-white',
    atual: 'border-zinc-900 bg-white shadow-card ring-4 ring-zinc-900/5',
    pendente: 'border-dashed border-zinc-300 bg-zinc-50/60',
  };

  return (
    <section
      id={id}
      aria-current={estado === 'atual' ? 'step' : undefined}
      className={[
        'scroll-mt-20 rounded-2xl border p-4 transition-colors animate-slide-up motion-reduce:animate-none',
        estilosPorEstado[estado],
      ].join(' ')}
    >
      <div className={`mb-3 ${destacar ? 'animate-shake-once motion-reduce:animate-none' : ''}`}>
        <h2 className='flex items-center gap-2 text-sm font-semibold text-zinc-900'>
          {estado === 'concluida' ? (
            <AiFillCheckCircle className='h-6 w-6 flex-none text-emerald-600' aria-hidden='true' />
          ) : (
            <span
              className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs ${estado === 'atual' ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-500'}`}
              aria-hidden='true'
            >
              {numero}
            </span>
          )}
          <span className='flex-1'>{titulo}</span>
          {estado === 'atual' && (
            <span className='rounded-full bg-zinc-900 px-2.5 py-0.5 text-xs font-medium text-white'>Etapa atual</span>
          )}
          {estado === 'concluida' && <span className='sr-only'>(concluído)</span>}
        </h2>
        {estado !== 'concluida' && (
          <p className={`mt-1 pl-8 text-sm ${estado === 'atual' ? 'text-zinc-700' : 'text-zinc-500'}`}>{instrucao}</p>
        )}
      </div>
      {children}
    </section>
  );
}

export default function NovaReserva({ slide }: { slide: string }) {
  const navigate = useNavigate();
  const { caminho } = useParams();
  const { usuario, setIsLoadingOverlay }: IContext = useContext(Context);

  const [tiposDeReserva, setTiposDeReserva] = useState<ITipRes[]>([]);
  const [tiposDeEspaco, setTiposDeEspaco] = useState<ITipEsp[]>([]);
  const [fotosDoEspaco, setFotosDoEspaco] = useState<IFotEsp[]>([]);
  const [tipoDeReservaSelecionada, setTipoDeReservaSelecionada] = useState<ITipRes | undefined>();
  const [tipoDeEspacoSelecionado, setTipoDeEspacoSelecionado] = useState<ITipEsp | undefined>();
  const [espacos, setEspacos] = useState<IEspaco[]>([]);
  const [espacoSelecionado, setEspacoSelecionado] = useState<IEspaco | undefined>();
  const [carregandoEspacos, setCarregandoEspacos] = useState(false);
  const [formData, setFormData] = useState<IFormData>({
    cgc: '',
    obs: '',
  });
  const [usuarioReserva, setUsuarioReserva] = useState<IUsuario | undefined>();
  const [statusBuscaUsuario, setStatusBuscaUsuario] = useState<StatusBuscaUsuario>('idle');
  const [outroUsuarioAberto, setOutroUsuarioAberto] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [secaoDestacada, setSecaoDestacada] = useState<string | undefined>();

  const isAdministrativo = usuario?.tip === 'ADMINISTRADOR' || usuario?.tip === 'ASSISTENTE';
  const podeConsultarDisponibilidade = !!(espacoSelecionado && tipoDeReservaSelecionada &&
    (!formData.cgc || (formData.cgc && formData.cgc.length >= 14 && usuarioReserva?.cod)));
  const exibindoAgenda = slide === 'agenda' && !!espacoSelecionado && !!tipoDeReservaSelecionada;
  const fotoCapa = fotosDoEspaco.find(foto => foto.codesp === espacoSelecionado?.cod);

  async function getTiposDeEspaco() {
    try {
      const response = await api.get('/tiposDeEspaco');

      if (response.status === 200) {
        setTiposDeEspaco(response.data);
      }
    } catch (error: any) {
      toast.error('Erro ao buscar Tipos de Espaço');
    }
  }
  async function getTiposDeReserva(codTipEsp: number) {
    try {
      setTipoDeReservaSelecionada(undefined);

      const response = await api.get('/tiposDeReserva', {
        params: {
          codtipesp: codTipEsp
        }
      });

      if (response.status === 200) {
        setTiposDeReserva(response.data);

        if (response.data.length === 1) {
          setTipoDeReservaSelecionada(response.data[0]);
        }
        if (response.data.length === 0) {
          toast.warning('Nenhum tipo de reserva encontrado. Comunique o administrador.');
        }
      }
    } catch (error: any) {
      toast.error('Erro ao buscar Tipos de Espaço');
    }
  }

  async function getEspacos(codTipEsp: number) {
    try {
      setEspacoSelecionado(undefined);
      setCarregandoEspacos(true);

      const response = await api.get('/espacos', {
        params: {
          codtipesp: codTipEsp,
          ativo: true
        }
      });

      if (response.status === 200) {
        setEspacos(response.data);
      }
    } catch (error) {
      toast.error('Erro ao buscar Espaços');
    } finally {
      setCarregandoEspacos(false);
    }
  }

  async function getFotosDoEspaço(codesp?: string | number) {
    try {
      if (!codesp) return;

      setIsLoadingOverlay(true);

      const response = await api.get('/fotesp', {
        params: {
          codesp
        }
      });

      if (response.status === 200) {
        setFotosDoEspaco(response.data);
      }
    } catch (error: any) {
      toast.error('Erro ao buscar Tipos de Espaço');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 200);
    }
  }

  async function getUsuario(cgc: string) {
    try {
      setIsLoadingOverlay(true);
      setStatusBuscaUsuario('buscando');
      const response = await api.get('/usuarios', {
        params: {
          cgc
        }
      });

      if (response.status === 200 && response.data.length > 0) {
        setUsuarioReserva(response.data[0]);
        setStatusBuscaUsuario('encontrado');
        return;
      }

      setUsuarioReserva(undefined);
      setStatusBuscaUsuario('naoEncontrado');
      toast.warning('Usuário não encontrado. Verifique o Cpf e tente novamente.');
    } catch (error) {
      setStatusBuscaUsuario('naoEncontrado');
      toast.error('Falha ao buscar usuário por cpf');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 200);
    }
  }

  // Primeira etapa ainda não preenchida: é o que o usuário precisa fazer agora para poder consultar a disponibilidade
  function proximaEtapa(): { idSecao: string, texto: string } | undefined {
    if (!tipoDeEspacoSelecionado) return { idSecao: 'secaoTipoEspaco', texto: 'Escolha o tipo de espaço' };
    if (!espacoSelecionado) return { idSecao: 'secaoEspaco', texto: 'Escolha o espaço' };
    if (!tipoDeReservaSelecionada) return { idSecao: 'secaoTipoReserva', texto: 'Escolha o tipo de reserva' };
    if (!podeConsultarDisponibilidade) return { idSecao: 'secaoAssociado', texto: 'Confirme o CPF/CNPJ do associado' };
    return undefined;
  }

  function estadoSecao(idSecao: string, preenchida: boolean): EstadoSecao {
    if (preenchida) return 'concluida';
    return proximaEtapa()?.idSecao === idSecao ? 'atual' : 'pendente';
  }

  // Leva o usuário até a etapa que falta e chama a atenção para ela
  function irParaProximaEtapa() {
    const etapa = proximaEtapa();
    if (!etapa) return;

    const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    document.getElementById(etapa.idSecao)?.scrollIntoView({ behavior, block: 'start' });

    setSecaoDestacada(undefined);
    setTimeout(() => setSecaoDestacada(etapa.idSecao), 50);
    setTimeout(() => setSecaoDestacada(undefined), 700);
  }

  function consultarDisponibilidade() {
    if (!podeConsultarDisponibilidade) {
      irParaProximaEtapa();
      return;
    }
    navigate(`/${caminho}/usuario/cadastro/novaReserva/agenda`);
  }

  useEffect(() => {
    getTiposDeEspaco();
  }, []);

  useEffect(() => {
    // Ao recarregar a página na agenda a seleção é perdida, volta para a primeira etapa
    if (slide === 'agenda' && (!espacoSelecionado || !tipoDeReservaSelecionada)) {
      navigate(`/${caminho}/usuario/cadastro/novaReserva`, { replace: true });
    }

    window.scrollTo({ top: 0 });
  }, [slide]);

  useEffect(() => {
    if (tipoDeEspacoSelecionado?.cod) {
      getEspacos(tipoDeEspacoSelecionado.cod);
      getTiposDeReserva(tipoDeEspacoSelecionado.cod);
    }
  }, [tipoDeEspacoSelecionado]);

  useEffect(() => {
    getFotosDoEspaço(espacoSelecionado?.cod);
  }, [espacoSelecionado]);

  if (exibindoAgenda) {
    return (
      <div className='w-full max-w-5xl mx-auto px-4 pb-10'>
        <Stepper etapaAtual={1} />
        <AgendaReserva
          espaco={espacoSelecionado}
          detalhesReserva={usuarioReserva ? { codusu: usuarioReserva.cod, usu: usuarioReserva.usu, obs: formData.obs } : undefined}
          tipoDeReserva={tipoDeReservaSelecionada}
          onVoltar={() => navigate(-1)}
        />
      </div>
    );
  }

  const etapaPendente = proximaEtapa();

  // Não usa "disabled": com o botão clicável, ao tentar prosseguir o usuário é levado até a etapa que falta
  const botaoConsultar = (
    <button
      type='button'
      onClick={consultarDisponibilidade}
      aria-disabled={!podeConsultarDisponibilidade}
      className={[
        'flex h-12 w-full items-center justify-center gap-2 rounded-xl px-5 font-semibold transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2',
        podeConsultarDisponibilidade
          ? 'bg-zinc-900 text-white hover:bg-zinc-700'
          : 'bg-zinc-200 text-zinc-400',
      ].join(' ')}
    >
      Consultar disponibilidade
      <AiOutlineArrowRight aria-hidden='true' />
    </button>
  );

  const avisoProximaEtapa = etapaPendente && (
    <button
      type='button'
      onClick={irParaProximaEtapa}
      className='flex w-full items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left text-sm text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
    >
      <AiOutlineInfoCircle className='h-4 w-4 flex-none text-amber-600' aria-hidden='true' />
      <span className='flex-1'>
        <span className='font-semibold'>Próximo passo:</span> {etapaPendente.texto.toLowerCase()}
      </span>
      <AiOutlineArrowRight className='h-4 w-4 flex-none text-amber-700' aria-hidden='true' />
    </button>
  );

  return (
    <div className='w-full max-w-5xl mx-auto px-4 pb-[calc(10rem+env(safe-area-inset-bottom))] md:pb-10'>
      <EspacoDetalhesModal
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        espaco={espacoSelecionado}
        fotos={fotosDoEspaco}
      />

      <Stepper etapaAtual={0} />

      <header className='mb-6'>
        <h1 className='text-2xl font-semibold text-zinc-900 md:text-3xl'>Nova reserva</h1>
        <p className='mt-1 text-zinc-500'>Escolha o espaço e o tipo de reserva para ver as datas disponíveis.</p>
      </header>

      <div className='grid gap-6 md:grid-cols-[1fr_340px] md:items-start'>
        <div className='space-y-8'>
          <Secao
            id='secaoTipoEspaco'
            numero={1}
            titulo='Tipo de espaço'
            estado={estadoSecao('secaoTipoEspaco', !!tipoDeEspacoSelecionado)}
            instrucao='Selecione o tipo de espaço que deseja reservar.'
            destacar={secaoDestacada === 'secaoTipoEspaco'}
          >
            <div role='radiogroup' aria-label='Tipo de espaço' className='grid gap-3 sm:grid-cols-2'>
              {tiposDeEspaco.map((tipoDeEspaco: ITipEsp) => (
                <OptionCard
                  key={tipoDeEspaco.cod}
                  titulo={tipoDeEspaco.tipesp}
                  descricao={textoSemHtml(tipoDeEspaco.det)}
                  selecionado={tipoDeEspacoSelecionado?.cod === tipoDeEspaco.cod}
                  onClick={() => {
                    rolarNoMobile('secaoEspaco');
                    if (tipoDeEspacoSelecionado?.cod === tipoDeEspaco.cod) return;
                    setTipoDeEspacoSelecionado(tipoDeEspaco);
                  }}
                />
              ))}
              {tiposDeEspaco.length === 0 && [1, 2].map(item => (
                <div key={item} className='h-14 animate-pulse rounded-xl bg-zinc-100' />
              ))}
            </div>
          </Secao>

          {tipoDeEspacoSelecionado && (
            <>
              <Secao
                id='secaoEspaco'
                numero={2}
                titulo='Espaço'
                estado={estadoSecao('secaoEspaco', !!espacoSelecionado)}
                instrucao='Selecione qual espaço deseja reservar.'
                destacar={secaoDestacada === 'secaoEspaco'}
              >
                <div role='radiogroup' aria-label='Espaço' className='grid gap-3 sm:grid-cols-2'>
                  {carregandoEspacos ? [1, 2].map(item => (
                    <div key={item} className='h-14 animate-pulse rounded-xl bg-zinc-100' />
                  )) : espacos.map((espaco: IEspaco) => (
                    <OptionCard
                      key={espaco.cod}
                      titulo={espaco.esp}
                      descricao={espaco.des}
                      selecionado={espacoSelecionado?.cod === espaco.cod}
                      onClick={() => {
                        // Com um único tipo de reserva ele já vem selecionado, então segue direto para o fim
                        rolarNoMobile(tiposDeReserva.length === 1 ? undefined : 'secaoTipoReserva');
                        if (espacoSelecionado?.cod === espaco.cod) return;
                        setEspacoSelecionado(espaco);
                      }}
                    />
                  ))}
                </div>
                {!carregandoEspacos && espacos.length === 0 && (
                  <p className='rounded-xl border border-dashed border-zinc-300 px-4 py-6 text-center text-sm text-zinc-500'>
                    Nenhum espaço ativo para este tipo.
                  </p>
                )}
                {(espacoSelecionado && espacoSelecionado.obs) && (
                  <div className='mt-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
                    <AiOutlineInfoCircle className='mt-0.5 h-5 w-5 flex-none text-amber-600' aria-hidden='true' />
                    <p><span className='font-semibold'>Obs:</span> {espacoSelecionado.obs}</p>
                  </div>
                )}
              </Secao>

              <Secao
                id='secaoTipoReserva'
                numero={3}
                titulo='Tipo de reserva'
                estado={estadoSecao('secaoTipoReserva', !!tipoDeReservaSelecionada)}
                instrucao='Selecione o tipo de reserva para ver as datas e valores disponíveis.'
                destacar={secaoDestacada === 'secaoTipoReserva'}
              >
                <div role='radiogroup' aria-label='Tipo de reserva' className='grid gap-3 sm:grid-cols-2'>
                  {tiposDeReserva.map((tipoDeReserva: ITipRes) => {
                    const menorValor = menorValorTipoReserva(tipoDeReserva);

                    return (
                      <OptionCard
                        key={tipoDeReserva.cod}
                        titulo={tipoDeReserva.des}
                        descricao={menorValor ? `A partir de ${formatCurrency(menorValor)}` : undefined}
                        selecionado={tipoDeReservaSelecionada?.cod === tipoDeReserva.cod}
                        onClick={() => {
                          setTipoDeReservaSelecionada(tipoDeReserva);
                          rolarNoMobile();
                        }}
                      />
                    );
                  })}
                </div>
                {!carregandoEspacos && tiposDeReserva.length === 0 && (
                  <p className='rounded-xl border border-dashed border-zinc-300 px-4 py-6 text-center text-sm text-zinc-500'>
                    Nenhum tipo de reserva para este tipo de espaço.
                  </p>
                )}
              </Secao>
            </>
          )}

          {(isAdministrativo && espacoSelecionado && tipoDeReservaSelecionada) && (
            <section id='secaoAssociado' className='scroll-mt-20 overflow-hidden rounded-2xl border border-zinc-200 bg-white animate-slide-up motion-reduce:animate-none'>
              <button
                type='button'
                onClick={() => setOutroUsuarioAberto(!outroUsuarioAberto)}
                aria-expanded={outroUsuarioAberto || !!formData.cgc}
                className='flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900'
              >
                <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-zinc-100 text-zinc-700'>
                  <AiOutlineUserAdd className='h-5 w-5' aria-hidden='true' />
                </span>
                <span className='flex-1'>
                  <span className='block font-semibold text-zinc-900'>Reservar para outro associado</span>
                  <span className='block text-sm text-zinc-500'>
                    {usuarioReserva?.usu ? `Associado: ${usuarioReserva.usu}` : 'Opcional — informe o CPF/CNPJ do titular'}
                  </span>
                </span>
                <AiOutlineDown
                  className={`h-4 w-4 text-zinc-500 transition-transform ${(outroUsuarioAberto || !!formData.cgc) ? 'rotate-180' : ''}`}
                  aria-hidden='true'
                />
              </button>

              {(outroUsuarioAberto || !!formData.cgc) && (
                <div className='space-y-4 border-t border-zinc-100 px-4 py-4'>
                  <div>
                    <label htmlFor='cgcTitular' className='mb-1 block text-sm font-medium text-zinc-700'>CPF/CNPJ do titular</label>
                    <input
                      id='cgcTitular'
                      type='tel'
                      inputMode='numeric'
                      required
                      maxLength={18}
                      placeholder='000.000.000-00'
                      onChange={e => {
                        setStatusBuscaUsuario('idle');
                        setFormData({
                          ...formData, cgc: e.target.value.length <= 14 ?
                            cpfMask(e.target.value) :
                            cnpjMask(e.target.value)
                        });
                      }}
                      value={formData.cgc}
                      onBlur={() => {
                        if (formData.cgc && formData.cgc.length >= 14) {
                          getUsuario(formData.cgc);
                          return;
                        }

                        setFormData({ ...formData, cgc: '' });
                        setUsuarioReserva(undefined);
                        setStatusBuscaUsuario('idle');
                      }}
                      className='h-12 w-full rounded-xl border border-zinc-300 bg-white px-4 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10'
                    />
                    <div aria-live='polite' className='mt-2 min-h-[20px] text-sm'>
                      {statusBuscaUsuario === 'buscando' && <span className='text-zinc-500'>Buscando associado…</span>}
                      {statusBuscaUsuario === 'encontrado' && usuarioReserva && (
                        <span className='inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700'>
                          <AiFillCheckCircle aria-hidden='true' /> {usuarioReserva.usu}
                        </span>
                      )}
                      {statusBuscaUsuario === 'naoEncontrado' && (
                        <span className='text-rose-600'>Associado não encontrado. Verifique o CPF/CNPJ.</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor='obsReserva' className='mb-1 block text-sm font-medium text-zinc-700'>Observação</label>
                    <textarea
                      id='obsReserva'
                      rows={2}
                      required
                      onChange={e => setFormData({ ...formData, obs: e.target.value })}
                      value={formData.obs}
                      className='w-full resize-none rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10'
                    />
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className='md:sticky md:top-20'>
          <div className='overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-card'>
            {espacoSelecionado ? (
              <div className='animate-fade-in motion-reduce:animate-none'>
                <button
                  type='button'
                  onClick={() => setModalVisible(true)}
                  className='group relative block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900'
                  aria-label={`Ver fotos e detalhes de ${espacoSelecionado.esp}`}
                >
                  {fotoCapa ? (
                    <img
                      src={fotoCapa.linfot}
                      alt={espacoSelecionado.esp}
                      className='aspect-video w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]'
                    />
                  ) : (
                    <div className='flex aspect-video w-full items-center justify-center bg-zinc-100 text-zinc-300'>
                      <AiOutlinePicture className='h-10 w-10' aria-hidden='true' />
                    </div>
                  )}
                </button>
                <div className='px-5 pt-4'>
                  <h3 className='text-lg font-semibold text-zinc-900'>{espacoSelecionado.esp}</h3>
                  {espacoSelecionado.det && (
                    <p className='mt-1 text-sm text-zinc-500 line-clamp-3'>{textoSemHtml(espacoSelecionado.det)}</p>
                  )}
                  <button
                    type='button'
                    onClick={() => setModalVisible(true)}
                    className='mt-2 text-sm font-semibold text-zinc-900 underline underline-offset-4 hover:text-zinc-600'
                  >
                    Ver fotos e detalhes
                  </button>
                </div>
              </div>
            ) : (
              <div className='flex aspect-video w-full flex-col items-center justify-center gap-2 bg-zinc-50 px-6 text-center text-sm text-zinc-400'>
                <AiOutlinePicture className='h-8 w-8' aria-hidden='true' />
                Selecione um espaço para ver fotos e detalhes
              </div>
            )}

            <div className='px-5 py-5'>
              <ResumoReserva
                itens={[
                  { label: 'Tipo de espaço', valor: tipoDeEspacoSelecionado?.tipesp },
                  { label: 'Espaço', valor: espacoSelecionado?.esp },
                  { label: 'Tipo de reserva', valor: tipoDeReservaSelecionada?.des },
                  ...(usuarioReserva?.usu ? [{ label: 'Associado', valor: usuarioReserva.usu }] : []),
                ]}
              />
              <div className='mt-5 hidden space-y-2 md:block'>
                {avisoProximaEtapa}
                {botaoConsultar}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className='fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur md:hidden'>
        {avisoProximaEtapa && <div className='mb-2'>{avisoProximaEtapa}</div>}
        {botaoConsultar}
      </div>
    </div>
  );
}
